import React, { useMemo, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { fmt } from './posV2Utils';
import {
  formatSavDate,
  matchesSavSearch,
  repairFinancials,
  savStatusTone,
} from './posV2SavUtils';

const PosV2SavRepairsTable = ({ repairs, selectedRef, onSelect }) => {
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState(false);

  const filtered = useMemo(
    () => repairs.filter((d) => matchesSavSearch(d, filter)),
    [repairs, filter],
  );
  const visible = expanded ? filtered : filtered.slice(0, 5);

  return (
    <div className="rounded-xl border border-white/10 bg-black/30 overflow-hidden text-gray-200">
      <div className="p-3 border-b border-white/10 flex flex-wrap items-center gap-2 justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          Réparations liées
        </h3>
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" size={14} />
          <input
            className="w-full pl-8 pr-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white text-sm"
            placeholder="REM, client, appareil…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500 border-b border-white/5">
              <th className="px-3 py-2 font-semibold">N° REM</th>
              <th className="px-3 py-2 font-semibold">Client</th>
              <th className="px-3 py-2 font-semibold">Appareil</th>
              <th className="px-3 py-2 font-semibold">Statut</th>
              <th className="px-3 py-2 font-semibold text-right">Acompte</th>
              <th className="px-3 py-2 font-semibold text-right">Reste</th>
              <th className="px-3 py-2 font-semibold">Réception</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((d) => {
              const { acompte, reste } = repairFinancials(d);
              const tone = savStatusTone(d.status);
              const isSelected = selectedRef === d.ref;
              return (
                <tr
                  key={d.ref}
                  className={`border-b border-white/5 cursor-pointer transition ${
                    isSelected ? 'bg-red-950/30' : 'hover:bg-white/5'
                  }`}
                  onClick={() => onSelect(d)}
                >
                  <td className="px-3 py-2.5 font-semibold text-red-400">{d.ref}</td>
                  <td className="px-3 py-2.5 text-gray-300 truncate max-w-[120px]">{d.client_name || '—'}</td>
                  <td className="px-3 py-2.5 text-gray-400 truncate max-w-[140px]">{d.model || '—'}</td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold border ${tone.className}`}>
                      {tone.label}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right text-emerald-400">{fmt(acompte)} €</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-red-400">{fmt(reste)} €</td>
                  <td className="px-3 py-2.5 text-gray-500 text-xs whitespace-nowrap">
                    {formatSavDate(d.created_at) || '—'}
                  </td>
                </tr>
              );
            })}
            {!visible.length && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-gray-500 text-sm">
                  Aucune réparation trouvée
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {filtered.length > 5 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full py-2.5 text-xs text-gray-400 hover:text-gray-200 border-t border-white/10 flex items-center justify-center gap-1"
        >
          {expanded ? 'Réduire la liste' : `Voir toutes les réparations (${filtered.length})`}
          <ChevronDown size={14} className={expanded ? 'rotate-180' : ''} />
        </button>
      )}
    </div>
  );
};

export default PosV2SavRepairsTable;
